export const algebra2Topic4 = {
  id: "exponential-logarithmic",
  title: "Exponential & Logarithmic Functions",
  standards: {
    commonCore: ["HSF-IF.C.7e", "HSF-BF.B.5", "HSF-LE.A.4"],
    nctm: ["Algebra — understand patterns, relations, and functions"],
    states: { NY: ["AII-F.IF.7e", "AII-F.BF.5"], OH: ["F.IF.7e", "F.BF.5"], PA: ["CC.2.2.HS.C.3"], VA: ["AII.9"], TX: ["A2.5A"] }
  },
  explanation: `**Mr. Augustine says:** "Exponential and logarithmic functions are INVERSES of each other — like addition and subtraction. Master one and you've mastered both!"

**Exponential Functions:** f(x) = aˣ (a > 0, a ≠ 1)
- a > 1: exponential GROWTH; 0 < a < 1: exponential DECAY
- Always passes through (0, 1); Domain: all reals; Range: y > 0
- Natural base: e ≈ 2.71828

**Logarithmic Functions:** logₐ(x) = y means aʸ = x
- log₁₀(x) = log(x); logₑ(x) = ln(x)
- Mr. Augustine's trick: "LOG = EXPONENT"

**Properties of Logarithms:**
1. Product Rule: logₐ(MN) = logₐ(M) + logₐ(N)
2. Quotient Rule: logₐ(M/N) = logₐ(M) − logₐ(N)
3. Power Rule: logₐ(Mⁿ) = n·logₐ(M)
4. logₐ(1) = 0; logₐ(a) = 1; logₐ(aˣ) = x

**Change of Base:** logₐ(x) = log(x)/log(a)

**Solving Exponential Equations:** Take log of both sides, use power rule.
**Solving Log Equations:** Convert to exponential form, then solve. ALWAYS check domain!`,
  practiceQuestions: [
    { question: "Evaluate: log₂(32)", solution: "2⁵=32.\n**Answer: 5**" },
    { question: "Convert to exponential form: log₅(125) = 3", solution: "5³=125.\n**Answer: 5³ = 125**" },
    { question: "Convert to logarithmic form: 4³ = 64", solution: "log₄(64)=3.\n**Answer: log₄(64) = 3**" },
    { question: "Expand: log(x²y³/z)", solution: "2log(x)+3log(y)−log(z).\n**Answer: 2log(x) + 3log(y) − log(z)**" },
    { question: "Condense: 3log(x) − 2log(y) + log(z)", solution: "log(x³z/y²).\n**Answer: log(x³z/y²)**" },
    { question: "Solve: 3ˣ = 81", solution: "81=3⁴ → x=4.\n**Answer: x = 4**" },
    { question: "Solve: 2ˣ = 50 (round to 4 decimal places)", solution: "x=log(50)/log(2)≈5.6439.\n**Answer: x ≈ 5.6439**" },
    { question: "Solve: log₄(x) = 3", solution: "x=4³=64.\n**Answer: x = 64**" },
    { question: "Solve: ln(x) = 5", solution: "x=e⁵≈148.41.\n**Answer: x = e⁵ ≈ 148.41**" },
    { question: "Solve: log(x) + log(x − 3) = 1", solution: "log[x(x−3)]=1 → x(x−3)=10 → x²−3x−10=0 → (x−5)(x+2)=0. x=5 (x=−2 invalid).\n**Answer: x = 5**" },
    { question: "Evaluate: log₃(1/27)", solution: "1/27=3⁻³ → log₃(3⁻³)=−3.\n**Answer: −3**" },
    { question: "Use change of base to evaluate log₇(100).", solution: "log(100)/log(7)=2/0.8451≈2.3665.\n**Answer: ≈ 2.3665**" },
    { question: "Solve: 5^(2x−1) = 125", solution: "125=5³ → 2x−1=3 → x=2.\n**Answer: x = 2**" },
    { question: "Solve: log₂(x + 3) = log₂(2x − 1)", solution: "Same base: x+3=2x−1 → x=4.\n**Answer: x = 4**" },
    { question: "A population doubles every 10 years. Starting at 5,000, what is the population after 30 years?", solution: "P=5000×2^(30/10)=5000×8=40,000.\n**Answer: 40,000**" },
    { question: "Evaluate: e⁰", solution: "Any base to the 0 power = 1.\n**Answer: 1**" },
    { question: "Solve: log(3x − 2) = 2", solution: "3x−2=100 → x=34.\n**Answer: x = 34**" },
    { question: "Simplify: ln(e⁷)", solution: "ln(eˣ)=x.\n**Answer: 7**" },
    { question: "Solve: 4^(x+1) = 8^x", solution: "2^(2x+2)=2^(3x) → 2x+2=3x → x=2.\n**Answer: x = 2**" },
    { question: "A car depreciates at 15% per year. If it costs $30,000 now, what is its value after 5 years?", solution: "V=30000×(0.85)⁵≈30000×0.4437≈$13,311.\n**Answer: ≈ $13,311**" }
  ]
};

export const algebra2Topic5 = {
  id: "trigonometric-functions",
  title: "Trigonometric Functions",
  standards: {
    commonCore: ["HSF-TF.A.1", "HSF-TF.A.2", "HSF-TF.A.3", "HSF-TF.A.4"],
    nctm: ["Geometry — use trigonometric relationships"],
    states: { NY: ["AII-F.TF.1", "AII-F.TF.2"], OH: ["F.TF.1", "F.TF.2"], PA: ["CC.2.2.HS.C.6"], VA: ["AII.10"], TX: ["P.2A"] }
  },
  explanation: `**Mr. Augustine says:** "Trigonometry is all about CIRCLES and ANGLES. Once you understand the unit circle, everything else falls into place!"

**The Unit Circle:** A circle with radius 1 centered at the origin. Any point: (cos θ, sin θ).

**Radian Measure:** 360° = 2π radians; 180° = π radians
- Degrees → radians: multiply by π/180
- Radians → degrees: multiply by 180/π

**The Six Trig Functions:**
- sin θ = opp/hyp; cos θ = adj/hyp; tan θ = opp/adj
- csc θ = 1/sin θ; sec θ = 1/cos θ; cot θ = 1/tan θ

**Key Values (MUST memorize!):**
- sin/cos/tan at 0°, 30°, 45°, 60°, 90°

**ASTC Rule (All Students Take Calculus):**
- Q I: ALL positive; Q II: SINE positive; Q III: TANGENT positive; Q IV: COSINE positive

**Pythagorean Identities:**
- sin²θ + cos²θ = 1; 1 + tan²θ = sec²θ; 1 + cot²θ = csc²θ

**Mr. Augustine's Tip:** "Memorize the unit circle values for 0°, 30°, 45°, 60°, 90° and you can find everything else!"`,
  practiceQuestions: [
    { question: "Convert 135° to radians.", solution: "135×π/180=3π/4.\n**Answer: 3π/4**" },
    { question: "Convert 5π/6 radians to degrees.", solution: "(5π/6)×(180/π)=150°.\n**Answer: 150°**" },
    { question: "Find sin(30°), cos(30°), and tan(30°).", solution: "sin=1/2, cos=√3/2, tan=√3/3.\n**Answer: sin=1/2, cos=√3/2, tan=√3/3**" },
    { question: "Find the exact value of sin(225°).", solution: "Q III, ref=45°. Sine negative. −√2/2.\n**Answer: −√2/2**" },
    { question: "Find the exact value of cos(300°).", solution: "Q IV, ref=60°. Cosine positive. 1/2.\n**Answer: 1/2**" },
    { question: "If sin θ = 3/5 and θ is in Q II, find cos θ and tan θ.", solution: "cos θ=−4/5 (negative in Q II). tan θ=−3/4.\n**Answer: cos θ = −4/5, tan θ = −3/4**" },
    { question: "Find the exact value of tan(120°).", solution: "Q II, ref=60°. Tangent negative. −√3.\n**Answer: −√3**" },
    { question: "Verify: sin²(60°) + cos²(60°) = 1.", solution: "(√3/2)²+(1/2)²=3/4+1/4=1 ✓\n**Answer: Verified**" },
    { question: "Find csc(30°).", solution: "csc=1/sin. sin(30°)=1/2. csc=2.\n**Answer: 2**" },
    { question: "Find the reference angle for 250°.", solution: "Q III: 250°−180°=70°.\n**Answer: 70°**" },
    { question: "Find the exact value of sin(7π/6).", solution: "7π/6=210°. Q III, ref=30°. Sine negative. −1/2.\n**Answer: −1/2**" },
    { question: "If cos θ = −5/13 and θ is in Q III, find sin θ.", solution: "sin²θ=1−25/169=144/169. Q III: sin=−12/13.\n**Answer: −12/13**" },
    { question: "Find the exact value of cos(−60°).", solution: "Cosine is even: cos(−60°)=cos(60°)=1/2.\n**Answer: 1/2**" },
    { question: "Find the exact value of sin(−π/2).", solution: "Sine is odd: sin(−π/2)=−sin(π/2)=−1.\n**Answer: −1**" },
    { question: "In which quadrant is θ if sin θ < 0 and cos θ > 0?", solution: "sin<0: Q III or IV. cos>0: Q I or IV. Both: Q IV.\n**Answer: Quadrant IV**" },
    { question: "Find sec(45°).", solution: "sec=1/cos. cos(45°)=√2/2. sec=√2.\n**Answer: √2**" },
    { question: "Find the exact value of tan(3π/4).", solution: "3π/4=135°. Q II, ref=45°. Tangent negative. −1.\n**Answer: −1**" },
    { question: "Find all angles θ in [0°, 360°) where sin θ = √3/2.", solution: "Ref=60°. Sine positive in Q I and Q II: 60° and 120°.\n**Answer: 60° and 120°**" },
    { question: "Find cot(π/3).", solution: "cot=cos/sin=(1/2)/(√3/2)=1/√3=√3/3.\n**Answer: √3/3**" },
    { question: "Find all angles θ in [0°, 360°) where tan θ = −1.", solution: "Ref=45°. Tangent negative in Q II and Q IV: 135° and 315°.\n**Answer: 135° and 315°**" }
  ]
};

export const algebra2Topic6 = {
  id: "trig-identities-equations",
  title: "Trigonometric Identities & Equations",
  standards: {
    commonCore: ["HSF-TF.B.5", "HSF-TF.C.8", "HSF-TF.C.9"],
    nctm: ["Algebra — use mathematical models"],
    states: { NY: ["AII-F.TF.5", "AII-F.TF.8"], OH: ["F.TF.5", "F.TF.8"], PA: ["CC.2.2.HS.C.7"], VA: ["AII.11"], TX: ["P.2C"] }
  },
  explanation: `**Mr. Augustine says:** "Trig identities are like secret codes — once you know them, you can transform any trig expression into something simpler!"

**Fundamental Identities:**
- Reciprocal: csc=1/sin, sec=1/cos, cot=1/tan
- Quotient: tan=sin/cos, cot=cos/sin
- Pythagorean: sin²θ+cos²θ=1; 1+tan²θ=sec²θ; 1+cot²θ=csc²θ
- Even/Odd: cos(−θ)=cosθ (even); sin(−θ)=−sinθ (odd)

**Sum & Difference Formulas:**
- sin(A±B) = sinAcosB ± cosAsinB
- cos(A±B) = cosAcosB ∓ sinAsinB

**Double Angle Formulas:**
- sin(2θ) = 2sinθcosθ
- cos(2θ) = cos²θ−sin²θ = 2cos²θ−1 = 1−2sin²θ

**Solving Trig Equations:**
1. Isolate the trig function
2. Find the reference angle
3. Use ASTC to find all solutions in [0°, 360°)

**Mr. Augustine's Strategy:** "When proving identities, work on ONE side only — usually the more complicated side. NEVER move things across the equal sign!"`,
  practiceQuestions: [
    { question: "Simplify: sin²θ + cos²θ + tan²θ", solution: "sin²θ+cos²θ=1. 1+tan²θ=sec²θ.\n**Answer: sec²θ**" },
    { question: "Prove: (1 − sin²θ)/cos θ = cos θ", solution: "1−sin²θ=cos²θ. cos²θ/cosθ=cosθ ✓\n**Answer: Proven**" },
    { question: "Find sin(75°) using the sum formula.", solution: "sin(45°+30°)=sin45cos30+cos45sin30=(√6+√2)/4.\n**Answer: (√6 + √2)/4**" },
    { question: "Find cos(15°) using the difference formula.", solution: "cos(45°−30°)=cos45cos30+sin45sin30=(√6+√2)/4.\n**Answer: (√6 + √2)/4**" },
    { question: "If sin θ = 3/5, find sin(2θ).", solution: "cosθ=4/5. sin(2θ)=2(3/5)(4/5)=24/25.\n**Answer: 24/25**" },
    { question: "Solve: 2sin θ − 1 = 0 for θ in [0°, 360°).", solution: "sinθ=1/2. Ref=30°. Q I and Q II: 30° and 150°.\n**Answer: 30° and 150°**" },
    { question: "Solve: cos²θ − cos θ = 0 for θ in [0°, 360°).", solution: "cosθ(cosθ−1)=0. cosθ=0→90°,270°. cosθ=1→0°.\n**Answer: 0°, 90°, 270°**" },
    { question: "Simplify: (sin θ + cos θ)² − 1", solution: "sin²θ+2sinθcosθ+cos²θ−1=1+2sinθcosθ−1=2sinθcosθ=sin(2θ).\n**Answer: sin(2θ)**" },
    { question: "Prove: tan θ · cos θ = sin θ", solution: "(sinθ/cosθ)·cosθ=sinθ ✓\n**Answer: Proven**" },
    { question: "Solve: 2cos²θ − 1 = 0 for θ in [0°, 360°).", solution: "cosθ=±√2/2. Ref=45°. θ=45°,135°,225°,315°.\n**Answer: 45°, 135°, 225°, 315°**" },
    { question: "Find cos(2θ) if cos θ = 4/5.", solution: "cos(2θ)=2cos²θ−1=2(16/25)−1=32/25−25/25=7/25.\n**Answer: 7/25**" },
    { question: "Simplify: sin(−θ)/cos(−θ)", solution: "sin(−θ)=−sinθ. cos(−θ)=cosθ. Result=−tanθ.\n**Answer: −tan θ**" },
    { question: "Solve: tan θ = √3 for θ in [0°, 360°).", solution: "Ref=60°. Tangent positive in Q I and Q III: 60° and 240°.\n**Answer: 60° and 240°**" },
    { question: "Prove: sec²θ − tan²θ = 1", solution: "From 1+tan²θ=sec²θ, rearrange: sec²θ−tan²θ=1 ✓\n**Answer: Proven**" },
    { question: "Find sin(A+B) if sinA=3/5, cosA=4/5, sinB=5/13, cosB=12/13.", solution: "sinAcosB+cosAsinB=(3/5)(12/13)+(4/5)(5/13)=36/65+20/65=56/65.\n**Answer: 56/65**" },
    { question: "Solve: 2sin²θ + sin θ − 1 = 0 for θ in [0°, 360°).", solution: "(2sinθ−1)(sinθ+1)=0. sinθ=1/2→30°,150°. sinθ=−1→270°.\n**Answer: 30°, 150°, 270°**" },
    { question: "Simplify: cos²θ − sin²θ", solution: "This is the double angle formula: cos(2θ).\n**Answer: cos(2θ)**" },
    { question: "Prove: (1 + tan²θ)·cos²θ = 1", solution: "sec²θ·cos²θ=(1/cos²θ)·cos²θ=1 ✓\n**Answer: Proven**" },
    { question: "Solve: sin(2θ) = sin θ for θ in [0°, 360°).", solution: "2sinθcosθ−sinθ=0 → sinθ(2cosθ−1)=0. sinθ=0→0°,180°. cosθ=1/2→60°,300°.\n**Answer: 0°, 60°, 180°, 300°**" },
    { question: "Find the general solution of cos θ = 0.", solution: "cosθ=0 at 90° and 270°. General: θ=90°+180°n.\n**Answer: θ = 90° + 180°n**" }
  ]
};
